import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Avatar,
  Chip,
  Tooltip,
  Popover,
  CircularProgress,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GemmaAvatar from './GemmaAvatar';
import { useNotification } from '../layouts/Layout';
import {
  fetchGemmaQueueJobs,
  patchGemmaQueueSchedule,
  deleteGemmaQueueJob,
  playGemmaQueueJob,
  pauseGemmaQueueJob,
  fetchGemmaQueueJobPreview,
  pollGemmaPrzedmiarJobUntilTerminal,
  type GemmaQueueJobDto,
  type GemmaQueueJobPreviewDto,
} from '../../services/gemmaQueueApi';
import { useAuth } from '../../contexts/AuthContext';
import { listAiJobs, AiJobDto } from '../../services/aiJobsService';
import { onAiJobsChanged } from '../../services/realtimeService';

export type GemmaQueueJobStatus = 'queued' | 'paused' | 'running' | 'done' | 'failed';

export type GemmaQueueJob = GemmaQueueJobDto;

export interface GemmaTasksDrawerProps {
  open: boolean;
  onClose: () => void;
  zIndex?: number;
  onEntered?: () => void;
}

const STATUS_LABEL: Record<GemmaQueueJobStatus, string> = {
  queued: 'W kolejce',
  paused: 'Wstrzymane',
  running: 'W toku',
  done: 'Wykonane',
  failed: 'Błąd',
};

/** Play / podgląd / usuń — nie może mieć minWidth:0 (siatka ściskała kolumnę do niewidocznej). */
const ACTION_COL_MIN_PX = 132;

const STATUS_CHIP_SX: Record<GemmaQueueJobStatus, { bgcolor: string; color: string }> = {
  queued: { bgcolor: '#E6F0FF', color: '#020242' },
  paused: { bgcolor: '#FFF5CC', color: '#020242' },
  running: { bgcolor: '#E6F7FF', color: '#020242' },
  done: { bgcolor: '#E8F5E9', color: '#1B5E20' },
  failed: { bgcolor: '#FFEBEE', color: '#C62828' },
};

export const GEMMA_QUEUE_NAME_MAX_LEN = 30;

/** Minimalna szerokość kolumny tytułu w siatce (ok. tyle znaków w typowej czcionce 13px). */
export const GEMMA_QUEUE_LIST_TITLE_MIN_CH = 22;

/** Inicjały jak w OnlineUsersAvatars (spójnie z resztą aplikacji). */
function requesterInitials(displayName: string) {
  const parts = String(displayName || '').split(/[\s._-]+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || 'U';
}

export function truncateGemmaQueueName(raw: string, maxLen: number = GEMMA_QUEUE_NAME_MAX_LEN): string {
  const s = String(raw ?? '');
  if (s.length <= maxLen) return s;
  if (maxLen < 2) return s.slice(0, maxLen);
  return `${s.slice(0, maxLen - 1)}\u2026`;
}

function toDatetimeLocalInputValue(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${day}T${h}:${min}:${sec}`;
}

function ts(iso: string | null | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function sortGemmaQueueJobs(jobs: GemmaQueueJob[]): GemmaQueueJob[] {
  const tier = (s: GemmaQueueJobStatus): number => {
    if (s === 'running') return 0;
    if (s === 'queued' || s === 'paused') return 1;
    if (s === 'done' || s === 'failed') return 2;
    return 1;
  };
  const created = (j: GemmaQueueJob) => {
    const t = new Date(j.createdAt || 0).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  return [...jobs].sort((a, b) => {
    const ta = tier(a.status);
    const tb = tier(b.status);
    if (ta !== tb) return ta - tb;
    const da = ts(a.scheduledAt) - ts(b.scheduledAt);
    if (da !== 0) return da;
    return created(a) - created(b);
  });
}

function formatScheduledAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const sec = String(d.getSeconds()).padStart(2, '0');
      return `${y}-${mo}-${day}, ${h}-${min}-${sec}`;
    }
    const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : String(iso);
  } catch {
    return String(iso);
  }
}

function schedulePrimaryLine(row: GemmaQueueJob): string {
  if (row.scheduleAfterLp != null && row.scheduleAfterLp > 0) {
    return `Po zadaniu nr ${row.scheduleAfterLp}`;
  }
  return formatScheduledAt(row.scheduledAt);
}

/** Jedna linia „czy coś się dzieje” + przybliżony postęp (znaki + czas ostatniego zapisu w DB). */
function formatRunningProgressLine(p: Pick<GemmaQueueJobPreviewDto, 'status' | 'outputCharCount' | 'progressUpdatedAt'>): string {
  if (p.status !== 'running') return '';
  const n = p.outputCharCount ?? 0;
  let timeStr: string | null = null;
  if (p.progressUpdatedAt) {
    try {
      const d = new Date(p.progressUpdatedAt);
      if (!Number.isNaN(d.getTime())) {
        timeStr = d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }
    } catch {
      timeStr = null;
    }
  }
  if (n === 0) {
    return timeStr
      ? `Gemma pracuje — jeszcze bez zapisanego fragmentu · ostatnia aktywność ${timeStr}`
      : 'Gemma pracuje — oczekiwanie na pierwszy fragment odpowiedzi…';
  }
  const countStr = `~${n.toLocaleString('pl-PL')} znaków odpowiedzi`;
  return timeStr ? `${countStr} · ostatni zapis ${timeStr}` : countStr;
}

/** Jak na liście / kontekście OF: identyfikator — miejscowość · nazwa zamówienia (albo z karty kosztorysu). */
function gemmaQueuePreviewOfferDisplay(job: GemmaQueueJob): { idPart: string; subtitle: string; hasOfferLink: boolean } {
  const trimJoin = (parts: Array<string | null | undefined>) =>
    parts
      .filter((x) => x != null && String(x).trim() !== '')
      .map((x) => String(x).trim())
      .join(' · ');
  const fromOffer = trimJoin([job.standaloneOffer?.offerLocation, job.standaloneOffer?.orderName]);
  const fromEst = trimJoin([job.estimateLocation, job.estimateObjectName]);
  const subtitle = fromOffer || fromEst || '—';
  const hasOfferLink = Boolean(job.standaloneOffer?.id);
  const idPart = job.standaloneOffer
    ? job.standaloneOffer.offerCode || job.standaloneOffer.id.substring(0, 8)
    : job.estimateNumber?.trim() || `Kosztorys #${job.estimateId}`;
  return { idPart, subtitle, hasOfferLink };
}

export default function GemmaTasksDrawer({ open, onClose, zIndex, onEntered }: GemmaTasksDrawerProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const canManageGemmaQueueJob = React.useCallback(
    (row: GemmaQueueJob | null | undefined): boolean => {
      if (!row || !user) return false;
      if (user.role === 'admin') return true;
      const oid = row.ownerUserId;
      if (oid == null || oid === '') return false;
      return String(oid) === String(user.user_id);
    },
    [user],
  );
  /** Zapobiega podwójnemu starcie tego samego streamu (play + przycisk w podglądzie). */
  const queueStreamLockRef = React.useRef(false);
  const [jobs, setJobs] = React.useState<GemmaQueueJob[]>([]);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const sortedJobs = React.useMemo(() => sortGemmaQueueJobs(jobs), [jobs]);
  const [scheduleAnchor, setScheduleAnchor] = React.useState<HTMLElement | null>(null);
  const [scheduleEditId, setScheduleEditId] = React.useState<string | null>(null);
  const [scheduleDraft, setScheduleDraft] = React.useState('');

  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewJob, setPreviewJob] = React.useState<GemmaQueueJob | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewLiveText, setPreviewLiveText] = React.useState('');
  const [previewSavedText, setPreviewSavedText] = React.useState<string | null>(null);
  const [previewErrorLine, setPreviewErrorLine] = React.useState<string | null>(null);
  const [previewHint, setPreviewHint] = React.useState<string | null>(null);
  const [previewProgressLine, setPreviewProgressLine] = React.useState('');
  const previewPollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const drawerStackZ = zIndex != null ? Number(zIndex) : null;
  /** Tooltips muszą być wyżej niż Paper drawera (domyślny Popper ~1500 trafia pod szufladę). */
  const gemmaTooltipPopperProps = React.useMemo(
    () => ({ sx: { zIndex: drawerStackZ != null ? drawerStackZ + 10 : 16000 } }),
    [drawerStackZ],
  );

  /**
   * Zadania „meetingowe” (ai_jobs JSON): transkrypcja → generacja notatki → ew. generacja zadań AI z notatki.
   * Trzymamy je osobno od kolejki PDF (inna domena, inny model danych), ale pokazujemy na górze listy,
   * bo zwykle toczą się „tu i teraz” w trakcie spotkania.
   */
  const [aiJobs, setAiJobs] = React.useState<AiJobDto[]>([]);
  const reloadAiJobs = React.useCallback(async () => {
    try {
      const list = await listAiJobs();
      setAiJobs(list);
    } catch {
      setAiJobs([]);
    }
  }, []);
  React.useEffect(() => {
    if (!open) return;
    void reloadAiJobs();
    const off = onAiJobsChanged(() => { void reloadAiJobs(); });
    return () => { try { off(); } catch {} };
  }, [open, reloadAiJobs]);

  const AI_JOB_KIND_LABEL: Record<AiJobDto['kind'], string> = {
    meeting_transcription: 'Transkrypcja spotkania',
    meeting_note_generate: 'Generowanie notatki (Gemma)',
    meeting_note_ai_tasks: 'Zadania AI z notatki (Gemma)',
  };
  const AI_JOB_STATUS_LABEL: Record<AiJobDto['status'], string> = {
    queued: 'W kolejce',
    running: 'W toku',
    done: 'Wykonane',
    failed: 'Błąd',
  };
  const AI_JOB_STATUS_SX: Record<AiJobDto['status'], { bgcolor: string; color: string }> = {
    queued: { bgcolor: '#E6F0FF', color: '#020242' },
    running: { bgcolor: '#E6F7FF', color: '#020242' },
    done: { bgcolor: '#E8F5E9', color: '#1B5E20' },
    failed: { bgcolor: '#FFEBEE', color: '#C62828' },
  };

  /** Sort: running → queued → done/failed, w obrębie poziomu wg updatedAt desc. */
  const sortedAiJobs = React.useMemo(() => {
    const tier = (s: AiJobDto['status']) => (s === 'running' ? 0 : s === 'queued' ? 1 : 2);
    return [...aiJobs].sort((a, b) => {
      const ta = tier(a.status);
      const tb = tier(b.status);
      if (ta !== tb) return ta - tb;
      const da = new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime();
      return Number.isNaN(da) ? 0 : da;
    });
  }, [aiJobs]);

  const reloadJobs = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await fetchGemmaQueueJobs();
      setJobs(list);
    } catch (e) {
      setLoadError((e as Error)?.message || 'Nie udało się wczytać kolejki');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    void reloadJobs();
  }, [open, reloadJobs]);

  React.useEffect(() => {
    const onRefresh = () => {
      if (open) void reloadJobs();
    };
    window.addEventListener('gemma-queue-changed', onRefresh);
    return () => window.removeEventListener('gemma-queue-changed', onRefresh);
  }, [open, reloadJobs]);

  const openScheduleEditor = (e: React.MouseEvent<HTMLElement>, row: GemmaQueueJob) => {
    if (!canManageGemmaQueueJob(row) || !row.canEditSchedule) return;
    const d = row.scheduledAt ? new Date(row.scheduledAt) : new Date();
    setScheduleDraft(Number.isNaN(d.getTime()) ? '' : toDatetimeLocalInputValue(d));
    setScheduleEditId(row.id);
    setScheduleAnchor(e.currentTarget);
  };

  const closeScheduleEditor = () => {
    setScheduleAnchor(null);
    setScheduleEditId(null);
    setScheduleDraft('');
  };

  const applySchedule = async () => {
    if (!scheduleEditId || !scheduleDraft) {
      closeScheduleEditor();
      return;
    }
    const next = new Date(scheduleDraft);
    if (Number.isNaN(next.getTime())) return;
    try {
      await patchGemmaQueueSchedule(scheduleEditId, next.toISOString());
      await reloadJobs();
      closeScheduleEditor();
    } catch (err) {
      showNotification((err as Error)?.message || 'Nie udało się zapisać terminu', 'error');
    }
  };

  /**
   * Start na serwerze (`play` + worker), podgląd postępu przez polling `last_gemma_output` — bez SSE.
   * Zamknięcie karty nie przerywa pracy na backendzie.
   */
  const runQueuePdfStream = async (row: GemmaQueueJob) => {
    if (!canManageGemmaQueueJob(row)) {
      showNotification('Nie masz uprawnień do zarządzania tym zadaniem.', 'error');
      return;
    }
    if (queueStreamLockRef.current) return;
    if (row.status !== 'queued' && row.status !== 'paused') {
      showNotification('Można uruchomić tylko zadanie w kolejce lub wstrzymane.', 'warning');
      return;
    }

    queueStreamLockRef.current = true;
    stopPreviewPoll();
    setPreviewErrorLine(null);
    setPreviewLoading(true);
    setPreviewHint('Przygotowanie: uruchomienie zadania i pliki PDF…');

    const jobId = row.id;
    try {
      await playGemmaQueueJob(jobId);
      const listAfterPlay = await fetchGemmaQueueJobs();
      setJobs(listAfterPlay);
      const u = listAfterPlay.find((j) => j.id === jobId);
      if (u) setPreviewJob(u);
      if (!u || u.status !== 'running') {
        throw new Error('Nie udało się ustawić zadania jako „w toku”. Spróbuj ponownie.');
      }

      setPreviewHint(
        'Gemma generuje odpowiedź na serwerze — poniżej widać zapisywany fragment JSON. Możesz zamknąć tę kartę; zadanie dokończy się po stronie backendu.'
      );
      setPreviewLiveText('');
      setPreviewProgressLine('Postęp: oczekiwanie na dane z serwera…');

      const terminal = await pollGemmaPrzedmiarJobUntilTerminal(
        jobId,
        (p) => {
          setPreviewLiveText(p.lastGemmaOutput || '');
          setPreviewProgressLine(formatRunningProgressLine(p));
          setPreviewErrorLine(p.errorMessage);
        },
        { intervalMs: 1500, maxWaitMs: 3 * 60 * 60 * 1000 }
      );

      const toParse = terminal.lastGemmaOutput || '';
      setPreviewSavedText(toParse);
      setPreviewLiveText('');
      setPreviewProgressLine('');

      if (terminal.status === 'done') {
        showNotification('Zadanie zakończone — przedmiar zapisany w kosztorysie.', 'success');
      } else {
        showNotification(terminal.errorMessage || 'Zadanie zakończone niepowodzeniem', 'error');
      }

      setPreviewHint(null);
    } catch (e) {
      const msg = (e as Error)?.message || 'Błąd importu PDF z kolejki';
      setPreviewHint(msg);
      showNotification(msg, 'error');
    } finally {
      queueStreamLockRef.current = false;
      setPreviewLoading(false);
      try {
        const list = await fetchGemmaQueueJobs();
        setJobs(list);
        const u = list.find((j) => j.id === jobId);
        if (u) setPreviewJob(u);
      } catch {
        await reloadJobs();
      }
      window.dispatchEvent(new CustomEvent('gemma-queue-changed'));
    }
  };

  const handlePlay = async (row: GemmaQueueJob) => {
    try {
      stopPreviewPoll();
      setPreviewJob(row);
      setPreviewOpen(true);
      setPreviewLiveText('');
      setPreviewSavedText(null);
      setPreviewErrorLine(null);
      setPreviewProgressLine('');
      setPreviewHint(null);
      await runQueuePdfStream(row);
    } catch (err) {
      showNotification((err as Error)?.message || 'Nie można uruchomić zadania', 'error');
    }
  };

  const handlePause = async (id: string) => {
    const row = jobs.find((j) => j.id === id);
    if (!row || !canManageGemmaQueueJob(row)) {
      showNotification('Nie masz uprawnień do zarządzania tym zadaniem.', 'error');
      return;
    }
    try {
      await pauseGemmaQueueJob(id);
      await reloadJobs();
    } catch (err) {
      showNotification((err as Error)?.message || 'Nie można wstrzymać', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    const row = jobs.find((j) => j.id === id);
    if (!row || !canManageGemmaQueueJob(row)) {
      showNotification('Nie masz uprawnień do zarządzania tym zadaniem.', 'error');
      return;
    }
    try {
      await deleteGemmaQueueJob(id);
      await reloadJobs();
    } catch (err) {
      showNotification((err as Error)?.message || 'Nie można usunąć', 'error');
    }
  };

  const stopPreviewPoll = React.useCallback(() => {
    if (previewPollRef.current != null) {
      clearInterval(previewPollRef.current);
      previewPollRef.current = null;
    }
  }, []);

  const closePreview = React.useCallback(() => {
    stopPreviewPoll();
    setPreviewOpen(false);
    setPreviewJob(null);
    setPreviewLiveText('');
    setPreviewSavedText(null);
    setPreviewErrorLine(null);
    setPreviewHint(null);
    setPreviewProgressLine('');
    setPreviewLoading(false);
  }, [stopPreviewPoll]);

  const loadSavedPreview = React.useCallback(async (jobId: string) => {
    setPreviewLoading(true);
    setPreviewSavedText(null);
    setPreviewErrorLine(null);
    setPreviewProgressLine('');
    try {
      const p = await fetchGemmaQueueJobPreview(jobId);
      setPreviewSavedText(p.lastGemmaOutput);
      setPreviewErrorLine(p.errorMessage);
    } catch (e) {
      setPreviewHint((e as Error)?.message || 'Nie udało się wczytać podglądu');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const startPreviewPoll = React.useCallback(
    (jobId: string) => {
      stopPreviewPoll();
      const tick = async () => {
        try {
          const p = await fetchGemmaQueueJobPreview(jobId);
          setPreviewSavedText(p.lastGemmaOutput);
          setPreviewErrorLine(p.errorMessage);
          setPreviewProgressLine(formatRunningProgressLine(p));

          if (p.status === 'done' || p.status === 'failed') {
            stopPreviewPoll();
            setPreviewHint(null);
            setPreviewProgressLine('');
            setPreviewJob((prev) => (prev && prev.id === jobId ? { ...prev, status: p.status } : prev));
            return;
          }

          if (p.status === 'running') {
            if (p.outputCharCount > 0) {
              setPreviewHint(
                'Pasek powyżej pokazuje przybliżony postęp (rozmiar zapisanego tekstu). Pełny JSON jest dopiero po zakończeniu — wtedy status zmieni się na „Wykonane”.'
              );
            } else {
              setPreviewHint(
                'Gemma przetwarza zadanie. Za chwilę pojawi się pierwszy fragment odpowiedzi; możesz zamknąć przeglądarkę — dokończenie jest po stronie serwera.'
              );
            }
          }
        } catch {
          /* kolejne próby */
        }
      };
      void tick();
      previewPollRef.current = setInterval(() => void tick(), 3500);
    },
    [stopPreviewPoll]
  );

  const openPreview = (row: GemmaQueueJob) => {
    stopPreviewPoll();
    setPreviewJob(row);
    setPreviewOpen(true);
    setPreviewLiveText('');
    setPreviewSavedText(null);
    setPreviewErrorLine(null);
    setPreviewHint(null);
    setPreviewProgressLine('');
    setPreviewLoading(false);

    if (row.status === 'done' || row.status === 'failed') {
      void loadSavedPreview(row.id);
    } else if (row.status === 'running') {
      setPreviewLoading(true);
      void (async () => {
        try {
          const p = await fetchGemmaQueueJobPreview(row.id);
          setPreviewSavedText(p.lastGemmaOutput);
          setPreviewErrorLine(p.errorMessage);
          setPreviewProgressLine(formatRunningProgressLine(p));
          startPreviewPoll(row.id);
          if (p.outputCharCount > 0) {
            setPreviewHint(
              'Pasek powyżej pokazuje przybliżony postęp (rozmiar zapisanego tekstu). Pełny JSON jest dopiero po zakończeniu — wtedy status zmieni się na „Wykonane”.'
            );
          } else {
            setPreviewHint(
              'Gemma przetwarza zadanie. Za chwilę pojawi się pierwszy fragment odpowiedzi; możesz zamknąć przeglądarkę — dokończenie jest po stronie serwera.'
            );
          }
        } catch (e) {
          setPreviewHint((e as Error)?.message || 'Błąd');
        } finally {
          setPreviewLoading(false);
        }
      })();
    } else {
      setPreviewHint(
        'W kolejce lub wstrzymane. Uruchom zadanie poniżej (jak ikona play na liście): Gemma, zapis na serwerze i import do kosztorysu. Możesz się wylogować w trakcie — backend dokończy pracę.'
      );
    }
  };

  React.useEffect(() => {
    return () => stopPreviewPoll();
  }, [stopPreviewPoll]);

  React.useEffect(() => {
    if (!open) closePreview();
  }, [open, closePreview]);

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      sx={{ zIndex: zIndex ?? ((theme) => theme.zIndex.drawer) }}
      SlideProps={{ onEntered: () => { try { onEntered?.(); } catch {} } } as any}
      onClose={onClose}
      ModalProps={{ disableEnforceFocus: true, disableAutoFocus: true, keepMounted: true, BackdropProps: { sx: { backgroundColor: 'rgba(0,0,0,0.35)' } } as any }}
      PaperProps={{
        sx: {
          width: 680,
          maxWidth: '100vw',
          minWidth: 320,
          top: '52px',
          height: 'calc(100vh - 52px)',
          borderRadius: 0,
          right: 0,
          left: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: zIndex ?? ((theme) => theme.zIndex.drawer),
        },
        onTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => {
          try {
            if ((e as any).propertyName === 'transform') onEntered?.();
          } catch {}
        },
      }}
    >
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: '#fff', borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          {previewOpen ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                <Tooltip title="Wróć do listy zadań" PopperProps={gemmaTooltipPopperProps}>
                  <IconButton onClick={closePreview} aria-label="Wróć do listy zadań" size="small" sx={{ mr: 0.5 }}>
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
                <GemmaAvatar size={22} spin={false} />
                <Typography
                  sx={{
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 500,
                    fontSize: '18px',
                    color: '#2D2D32',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {previewJob ? `Podgląd — ${truncateGemmaQueueName(previewJob.title, 36)}` : 'Podgląd'}
                </Typography>
              </Box>
              <IconButton onClick={onClose} aria-label="Zamknij szufladę">
                <CloseIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GemmaAvatar size={22} spin={false} />
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
                  {truncateGemmaQueueName('Zadania Gemmy')}
                </Typography>
              </Box>
              <IconButton onClick={onClose} aria-label="Zamknij">
                <CloseIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {previewOpen ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            px: 2,
            pt: 2,
            pb: 2,
          }}
        >
          {previewJob && (
            <>
              <Chip
                size="small"
                label={STATUS_LABEL[previewJob.status]}
                sx={{ mb: 1.5, alignSelf: 'flex-start', ...STATUS_CHIP_SX[previewJob.status] }}
              />
              <Paper
                elevation={0}
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: '#f9f9fb',
                  border: '1px solid #e8e8ed',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                  <Tooltip
                    title={previewJob.requester.displayName}
                    disableHoverListener={previewJob.requester.displayName.length <= GEMMA_QUEUE_NAME_MAX_LEN}
                    PopperProps={gemmaTooltipPopperProps}
                  >
                    <Avatar
                      alt={previewJob.requester.displayName}
                      src={previewJob.requester.avatarUrl || undefined}
                      sx={{ width: 36, height: 36, fontSize: '0.9rem' }}
                    >
                      {!previewJob.requester.avatarUrl ? requesterInitials(previewJob.requester.displayName) : null}
                    </Avatar>
                  </Tooltip>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3 }}>
                      Zlecający
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D2D32', lineHeight: 1.35 }}>
                      {previewJob.requester.displayName}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#595966', lineHeight: 1.5, mb: 0.75 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: '#2D2D32' }}>
                    Kosztorys:{' '}
                  </Box>
                  {previewJob.estimateName ?? '—'}
                  {' · '}
                  <Link
                    component={RouterLink}
                    to={`/app/easy-estimate/estimate/${previewJob.estimateId}`}
                    underline="hover"
                    sx={{ fontWeight: 500, fontSize: 'inherit' }}
                  >
                    Otwórz kosztorys
                  </Link>
                  {previewJob.estimateBranza != null && previewJob.estimateBranza !== '' ? (
                    <>
                      {' '}
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        ·
                      </Box>{' '}
                      <Box component="span" sx={{ fontWeight: 600, color: '#2D2D32' }}>
                        Branża:{' '}
                      </Box>
                      {previewJob.estimateBranza}
                    </>
                  ) : null}
                </Typography>
                {(() => {
                  const { idPart, subtitle, hasOfferLink } = gemmaQueuePreviewOfferDisplay(previewJob);
                  return (
                    <Typography variant="body1" sx={{ color: '#2D2D32', lineHeight: 1.5, mb: 0, fontWeight: 400 }}>
                      {idPart} — {subtitle}
                      {hasOfferLink ? (
                        <>
                          {' · '}
                          <Link
                            component={RouterLink}
                            to={`/app/easy-estimate/offer/${encodeURIComponent(previewJob.standaloneOffer!.id)}`}
                            underline="hover"
                            sx={{ fontWeight: 500, fontSize: 'inherit' }}
                          >
                            Otwórz ofertę
                          </Link>
                        </>
                      ) : null}
                    </Typography>
                  );
                })()}
                {previewJob.pdfFiles && previewJob.pdfFiles.length > 0 ? (
                  <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid #e8e8ed' }}>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary', mb: 0.75, fontWeight: 600 }}
                    >
                      Pliki PDF
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.25, color: '#595966' }}>
                      {previewJob.pdfFiles.map((pf, idx) => (
                        <Box component="li" key={`${pf.name}-${idx}`} sx={{ mb: 0.5, fontSize: '13px' }}>
                          <Box component="span" sx={{ color: '#2D2D32' }}>{pf.name}</Box>
                          {pf.driveWebViewLink ? (
                            <>
                              {' · '}
                              <Link
                                href={pf.driveWebViewLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                sx={{ fontWeight: 500, fontSize: 'inherit' }}
                              >
                                Otwórz w Google Drive
                              </Link>
                            </>
                          ) : (
                            <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                              (brak linku — kosztorys bez oferty / folderu Drive lub starsze zadanie)
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Paper>
            </>
          )}
          {previewProgressLine && previewJob?.status === 'running' && (
            <Typography variant="body2" sx={{ color: '#1565C0', mb: 1, fontWeight: 500, lineHeight: 1.45 }}>
              {previewProgressLine}
            </Typography>
          )}
          {previewHint && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.45 }}>
              {previewHint}
            </Typography>
          )}
          {previewErrorLine && (
            <Typography variant="body2" color="error" sx={{ mb: 1, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
              {previewErrorLine}
            </Typography>
          )}
          {(previewLoading || (previewJob?.status === 'running' && !previewSavedText && !previewLiveText)) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                ładowanie…
              </Typography>
            </Box>
          )}
          {previewJob && (previewJob.status === 'queued' || previewJob.status === 'paused') && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={previewLoading || !canManageGemmaQueueJob(previewJob)}
                onClick={() => previewJob && void runQueuePdfStream(previewJob)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', borderColor: '#4040DB', color: '#4040DB' }}
              >
                Uruchom zadanie
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={previewLoading}
                onClick={() => previewJob && navigate(`/app/easy-estimate/estimate/${previewJob.estimateId}`)}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Otwórz kosztorys
              </Button>
            </Box>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75 }}>
            Surowa odpowiedź modelu (JSON / tekst ze strumienia)
          </Typography>
          <Box
            component="pre"
            sx={{
              flex: 1,
              minHeight: 160,
              m: 0,
              p: 1.5,
              overflow: 'auto',
              bgcolor: '#f7f7f9',
              border: '1px solid #e8e8ed',
              borderRadius: '6px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '12px',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: '#1a1a22',
            }}
          >
            {previewSavedText || previewLiveText || (previewLoading ? '' : '—')}
          </Box>
        </Box>
      ) : (
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pt: '30px', pb: 2 }}>
        {sortedAiJobs.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#8a8a95', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Zadania spotkań
            </Typography>
            <Paper
              elevation={0}
              sx={{ border: '1px solid #eef0f4', borderRadius: '6px', overflow: 'hidden', bgcolor: '#fff' }}
            >
              {sortedAiJobs.map((job, idx) => (
                <Box
                  key={job.id}
                  sx={{
                    display: 'grid',
                    alignItems: 'center',
                    columnGap: 1,
                    px: 1.5,
                    py: 1,
                    gridTemplateColumns: '28px minmax(0, 1fr) max-content',
                    borderBottom: idx === sortedAiJobs.length - 1 ? 'none' : '1px solid #f2f3f5',
                    '&:hover': { bgcolor: '#fafafa' },
                  }}
                >
                  <Avatar
                    src={job.ownerAvatarUrl || undefined}
                    sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                  >
                    {!job.ownerAvatarUrl ? requesterInitials(job.ownerDisplayName || 'U') : null}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Tooltip title={job.title} enterDelay={400} PopperProps={gemmaTooltipPopperProps}>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: 13,
                          color: '#2D2D32',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 500,
                        }}
                      >
                        {job.title}
                      </Typography>
                    </Tooltip>
                    <Typography
                      sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: 11,
                        color: '#8a8a95',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {AI_JOB_KIND_LABEL[job.kind]}
                      {job.errorMessage ? ` · ${job.errorMessage}` : ''}
                    </Typography>
                  </Box>
                  <Chip
                    label={AI_JOB_STATUS_LABEL[job.status]}
                    size="small"
                    sx={{
                      justifySelf: 'end',
                      height: 22,
                      borderRadius: '4px',
                      ...AI_JOB_STATUS_SX[job.status],
                      '& .MuiChip-label': { px: 1, py: 0, fontSize: 11, fontWeight: 400 },
                    }}
                  />
                </Box>
              ))}
            </Paper>
          </Box>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && loadError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {loadError}
          </Typography>
        )}
        {!loading && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #fff',
            borderRadius: '4px',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {jobs.length === 0 && !loadError ? (
            <Box sx={{ py: 4, px: 2, color: 'text.secondary', textAlign: 'center', fontSize: '13px' }}>
              Brak pozycji w kolejce. Dodaj przedmiar z PDF w kosztorysie EasyEstimate.
            </Box>
          ) : (
            sortedJobs.map((row, index) => {
              const lp = index + 1;
              const canManage = canManageGemmaQueueJob(row);
              const showPlayPause = row.status === 'queued' || row.status === 'paused' || row.status === 'running';
              const showPlay = row.status === 'queued' || row.status === 'paused';

              return (
                <Box
                  key={row.id}
                  sx={{
                    display: 'grid',
                    alignItems: 'center',
                    justifyItems: 'stretch',
                    columnGap: 1,
                    rowGap: 0.5,
                    px: 1.5,
                    py: 1.25,
                    gridTemplateColumns: `28px minmax(0, 1fr) 40px max-content max-content minmax(${ACTION_COL_MIN_PX}px, max-content)`,
                    borderBottom: '1px solid #fff',
                    '&:last-of-type': { borderBottom: 'none' },
                    '&:hover': { bgcolor: '#fafafa' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '13px',
                      color: '#8a8a95',
                      textAlign: 'left',
                      gridColumn: 1,
                    }}
                  >
                    {lp}.
                  </Typography>
                  <Box sx={{ gridColumn: 2, minWidth: 0, overflow: 'hidden' }}>
                    <Tooltip title={row.title} enterDelay={400} PopperProps={gemmaTooltipPopperProps}>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '13px',
                          color: '#2D2D32',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.title}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box
                    sx={{
                      gridColumn: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      width: 40,
                      justifySelf: 'start',
                    }}
                  >
                    <Tooltip
                      title={row.requester.displayName}
                      disableHoverListener={row.requester.displayName.length <= GEMMA_QUEUE_NAME_MAX_LEN}
                      PopperProps={gemmaTooltipPopperProps}
                    >
                      <Avatar
                        alt={row.requester.displayName}
                        src={row.requester.avatarUrl || undefined}
                        sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                      >
                        {!row.requester.avatarUrl ? requesterInitials(row.requester.displayName) : null}
                      </Avatar>
                    </Tooltip>
                  </Box>
                  <Box
                    sx={{
                      gridColumn: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 0.25,
                      flexShrink: 0,
                      justifySelf: 'start',
                    }}
                  >
                    <Tooltip
                      title={
                        !canManage
                          ? 'Tylko właściciel lub administrator może zmienić harmonogram'
                          : row.canEditSchedule
                            ? 'Zmień datę i godzinę planowanego startu'
                            : 'Kolejka po wcześniejszym zadaniu — bez konkretnej godziny'
                      }
                      PopperProps={gemmaTooltipPopperProps}
                    >
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Edytuj planowany start (data i godzina)"
                          disabled={!row.canEditSchedule || !canManage}
                          onClick={(e) => openScheduleEditor(e, row)}
                          sx={{ p: 0.35, color: row.canEditSchedule && canManage ? 'text.secondary' : 'action.disabled' }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '12px',
                        lineHeight: 1.2,
                        color: '#595966',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {schedulePrimaryLine(row)}
                    </Typography>
                  </Box>
                  <Chip
                    label={STATUS_LABEL[row.status]}
                    size="small"
                    sx={{
                      gridColumn: 5,
                      justifySelf: 'start',
                      width: 'fit-content',
                      maxWidth: '100%',
                      height: 22,
                      borderRadius: '4px',
                      ...STATUS_CHIP_SX[row.status],
                      '& .MuiChip-label': {
                        px: 1,
                        py: 0,
                        fontSize: '11px',
                        fontWeight: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      gridColumn: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0,
                      flexShrink: 0,
                      minWidth: ACTION_COL_MIN_PX,
                      width: '100%',
                      justifySelf: 'stretch',
                      pr: 0,
                    }}
                  >
                    {showPlayPause ? (
                      showPlay ? (
                        <Tooltip
                          title={
                            canManage
                              ? 'Uruchom zadanie (Gemma + zapis do kosztorysu, w tej szufladzie)'
                              : 'Tylko właściciel lub administrator może uruchomić to zadanie'
                          }
                          PopperProps={gemmaTooltipPopperProps}
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Uruchom zadanie"
                              disabled={!canManage}
                              onClick={() => void handlePlay(row)}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={canManage ? 'Wstrzymaj (symulacja kolejki)' : 'Tylko właściciel lub administrator może wstrzymać to zadanie'}
                          PopperProps={gemmaTooltipPopperProps}
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Wstrzymaj"
                              disabled={!canManage}
                              onClick={() => void handlePause(row.id)}
                            >
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )
                    ) : (
                      <IconButton
                        size="small"
                        disabled
                        tabIndex={-1}
                        aria-hidden
                        sx={{ visibility: 'hidden', pointerEvents: 'none' }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip title="Podgląd: co robi / co zwróciła Gemma (strumień lub zapisany wynik)" PopperProps={gemmaTooltipPopperProps}>
                      <IconButton size="small" aria-label="Podgląd odpowiedzi Gemmy" onClick={() => openPreview(row)}>
                        <VisibilityIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        !canManage
                          ? 'Tylko właściciel lub administrator może usunąć to zadanie'
                          : row.status === 'running'
                            ? 'Nie można usunąć zadania w toku'
                            : row.status === 'done' || row.status === 'failed'
                              ? 'Usuń wpis z kolejki'
                              : 'Usuń z kolejki'
                      }
                      PopperProps={gemmaTooltipPopperProps}
                    >
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Usuń"
                          disabled={row.status === 'running' || !canManage}
                          onClick={() => void handleDelete(row.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>
        )}
      </Box>
      )}

      <Popover
        open={Boolean(scheduleAnchor && scheduleEditId)}
        anchorEl={scheduleAnchor}
        onClose={closeScheduleEditor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEnforceFocus
        disableAutoFocus
        disableScrollLock
        sx={{ zIndex: drawerStackZ != null ? drawerStackZ + 25 : 2147483300 }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            borderRadius: '8px',
            border: '1px solid #ededed',
          },
        }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#2D2D32', mb: 0.5 }}>
          Planowany start
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.25, lineHeight: 1.35 }}>
          Data i godzina (czas lokalny), z sekundami.
        </Typography>
        <input
          type="datetime-local"
          step={1}
          value={scheduleDraft}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScheduleDraft(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            padding: '8px 10px',
            borderRadius: 4,
            border: '1px solid #d4d4d9',
            color: '#2D2D32',
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
          <Button size="small" onClick={closeScheduleEditor} sx={{ textTransform: 'none' }}>
            Anuluj
          </Button>
          <Button size="small" variant="contained" onClick={() => void applySchedule()} sx={{ textTransform: 'none', bgcolor: '#4040DB' }}>
            Zapisz
          </Button>
        </Box>
      </Popover>
    </Drawer>
  );
}
